/**
 * ChartSurface
 * Classification: third-party-wrapper
 *
 * The ECharts lifecycle host, and the ONLY module importing the echarts
 * runtime. Everything else in `src/charts/` deals in plain option objects,
 * which is what keeps them testable in the node project.
 *
 * Also the tier-4 escape hatch: pass a raw `option` for anything the config
 * schema does not yet model (geo maps, custom series).
 */
import { useEffect, useRef, type CSSProperties } from 'react';
import * as echarts from 'echarts';
import type { EChartsOption } from 'echarts';
import { useTheme } from '@mui/material/styles';
import type { ChartMedia, ChartMode } from '@/tokens/chart';
import type { ChartPoint, ChartRow } from './types';
import { withAccessibleName } from './buildOption/accessibleName';

export interface ChartSurfaceProps {
  option: EChartsOption;
  mode?: ChartMode;
  media?: ChartMedia;
  height?: number | string;
  style?: CSSProperties;
  onPointClick?: (point: ChartPoint) => void;
  /** Accessible name for the chart region. */
  label?: string;
}

/**
 * `echarts` is an OPTIONAL peer dependency, so it can legitimately be absent.
 *
 * If it is not installed at all, the module specifier fails to resolve and the
 * bundler or runtime raises its own error — which already names `echarts`, so
 * there is nothing useful to add. What this guards is the nastier case: the
 * module resolves but is not a usable ECharts build (a version mismatch, a
 * mangled optional-dependency install, or a test environment that stubbed it).
 * Without this the failure surfaces as `echarts.init is not a function` deep in
 * an effect, with no indication that a peer dependency is the cause.
 */
function assertEChartsAvailable(): void {
  if (typeof echarts?.init !== 'function') {
    throw new Error(
      'Charts from `@aistrike-dev/ui/charts` require the optional peer dependency ' +
        '`echarts@^6.1.0`, which resolved but does not expose `init()`. Install or ' +
        'repair it with `npm install echarts@^6.1.0`. Components that need no chart ' +
        'library (Sparkline, Meter, SplitBar, StatTile) ship from `@aistrike-dev/ui` ' +
        'instead and do not require this dependency.',
    );
  }
}

interface EChartsClickParams {
  seriesId?: string;
  seriesName?: string;
  name?: string;
  value?: unknown;
  data?: unknown;
}

export function ChartSurface({
  option,
  mode,
  media = 'screen',
  height = 320,
  style,
  onPointClick,
  label,
}: ChartSurfaceProps) {
  const theme = useTheme();
  const resolvedMode: ChartMode = mode ?? (theme.palette.mode === 'light' ? 'light' : 'dark');
  const hostRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);
  // Held in a ref so a changing callback identity never tears down the chart.
  // Synced in an effect rather than during render: mutating a ref while
  // rendering is unsafe once concurrent rendering can discard the pass.
  const clickRef = useRef(onPointClick);
  useEffect(() => {
    clickRef.current = onPointClick;
  }, [onPointClick]);

  // Init and teardown. The renderer is fixed for an instance's lifetime, so a
  // media change must dispose and re-init — hence `media` in the dependencies.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;

    assertEChartsAvailable();

    const instance = echarts.init(host, undefined, {
      renderer: media === 'export' ? 'svg' : 'canvas',
    });
    chartRef.current = instance;

    instance.on('click', (params: unknown) => {
      const p = params as EChartsClickParams;
      const numeric = typeof p.value === 'number' ? p.value : Number(p.value);
      clickRef.current?.({
        seriesId: p.seriesId ?? '',
        seriesName: p.seriesName ?? '',
        x: p.name ?? '',
        y: Number.isFinite(numeric) ? numeric : 0,
        row: (p.data ?? {}) as ChartRow,
      });
    });

    // Export renders once at a known size for a headless capture; observing
    // resize there would only add nondeterminism.
    let observer: ResizeObserver | undefined;
    if (media === 'screen') {
      // Charts often mount before their container has its final size — this is
      // required inside grid layouts, where a one-shot init leaves them tiny.
      observer = new ResizeObserver(() => instance.resize());
      observer.observe(host);
    }

    return () => {
      observer?.disconnect();
      instance.dispose();
      chartRef.current = null;
    };
  }, [media, resolvedMode]);

  // `label` participates because ECharts writes the host's `aria-label` itself;
  // see `withAccessibleName` for why it has to be told the name rather than
  // left to generate one over the top of React's attribute.
  useEffect(() => {
    chartRef.current?.setOption(withAccessibleName(option, label), {
      notMerge: true,
      lazyUpdate: media === 'screen',
    });
  }, [option, media, label]);

  return (
    <div
      ref={hostRef}
      role="img"
      aria-label={label}
      style={{ width: '100%', height, minHeight: 0, ...style }}
    />
  );
}
